import { getPageProps, logger as Logger } from '@kibt/krn-utils';
import { currentCountry, getQuery, deviceId, setDeviceId } from '@/common/const';
import { getCookie } from '@/common/cookie';
import { getHalfByQuery, isWeb } from './device-info';
import { setLogger } from '@k18n/logger';
import { getBundleInfo } from './bridge/getBundleInfo';
import { setCookie } from '@kwr/utils-krn';

const radarConf = {
    // https://radar-plus.corp.kuaishou.com/project/0acc27da1e/edit?env=h5&timeRange=Last1Hour
    projectId: 'c6a11c949i',
    // 上报采样率，1 为全部上报
    sampling: 1,
};
// krn环境读取是固定的值，去掉从.env读取逻辑
const KDS_APP_LOG_TYPE = 'oversea';
const isProduction = true;
console.log('KDS_APP_LOG_TYPE::::', KDS_APP_LOG_TYPE, isProduction);
// WebLogger内核SDK配置参数
const logConfig = {
    env: KDS_APP_LOG_TYPE || 'development',
    proto: 'v3',
    autoPV: false,
    fps: false,
    logger: !isProduction,
    radar: radarConf,
    plugins: [],
};
const kpn = getCookie('kpn') || 'KWAI';

export const bucket = getCookie('bucket') || 'UNKNOWN';

// 公参配置
const baseOptions = {
    product_name: kpn,
    package_name: kpn,
    // user_id: userId,
    h5_extra_attr: {
        county_name: {
            country: ['BR', 'BRA'].includes(currentCountry) ? 'BRA' : currentCountry?.toUpperCase() || 'UNKNOWN',
            bucket: getCookie('bucket') || 'UNKNOWN',
        },
    },
};

/**
 * 只能初始化之后调用，即运行时调用，组件渲染后
 */
export let webLogger: Logger;

// export let radar;
export const initWebLogger = (
    props: any = {},
    config: {
        navigation?: any;
        pageMap?: any;
    } = {}
) => {
    const { componentName, bundleId } = props;
    const autoPvConfig = {
        componentName: componentName, // 必填
        bundleId: bundleId, // 必填
        coPage: false, // 非必填 ,半屏为true
        debug: true, // 非必填，打印打点信息
        // navigation, // 非必填 使用 react-navigation 需要传入 navigation Ref
        // pageMap: autoPvPageMap, // 每次 autoPv 上报埋点时都会先经过 pageMap，可在这里选择是否过滤掉埋点，返回 false 则不上报
        ...config,
    };

    const options: any = {
        baseOptions,
        logConfig,
        ...props,
    };

    // 如果没有传pageMap，说明是异步PV， 异步PV初始化Weblogger不需要传autoPvConfig
    if (config.pageMap) {
        options.autoPvConfig = autoPvConfig;
    }

    // 初始化weblogger
    const logInstance = new Logger(options);
    webLogger = logInstance;

    const deviceId = webLogger?.webLogger?.baseOption?.device_id as string;
    setDeviceId(deviceId);

    // 给 k18n-utils 传 logger
    setLogger(webLogger);
};

export const getWebLogger = () => {
    return webLogger?.webLogger;
};

/**
 * log埋点增加全局的版本号，排查故障的时候可以统一
 */
let commonParams = null;

export const getCommonParams = () => {
    if (commonParams) return { commonParams };

    const bundleInfo = getBundleInfo();
    commonParams = Object.assign({}, { container: 'rn' }, bundleInfo);
    return { commonParams };
};

export const radar = {
    event(args: { name: string; eventType?: string; params?: object }) {
        const { name, eventType = 'SHOP_KRN_CUSTOM_EVENT', params = {} } = args || {};
        webLogger?.webLogger?.plugins?.radar?.event({
            name,
            eventName: name,
            eventType,
            event_type: eventType,
            extra: {
                ...getCommonParams(),
                ...params,
            },
            extra_info: {
                ...getCommonParams(),
                ...params,
            },
        });
    },
    fmp(timesteamp?: number) {
        webLogger?.webLogger?.plugins?.radar?.fmp(timesteamp);
    },
};

export interface PageMapParams {
    page: string;
    params?: {
        [key: string]: any;
    };
    contentPackage?: {
        [key: string]: any;
    };
    feedLogCtx?: {
        [key: string]: any;
    };
}

export interface KRNAutoPVConfig {
    navigation?: any;
    bundleId: string;
    componentName: string;
    coPage?: boolean;
    debug?: boolean;
    pageMap: ({ page, params }: PageMapParams) => PageMapParams;
}

type ISendClickArgs = {
    // 页面动作标识
    action: string;
    // 额外参数
    params?: object;
    isSendRadar?: boolean;
    // 自定义雷达event的名字
    radarName?: string;
};

type ISendTaskArgs = {
    // 页面动作标识
    action: string;
    // 额外参数
    params?: object;
    isSendRadar?: boolean;
    // 自定义雷达event的名字
    radarName?: string;
    type?: 'backgroundTask' | 'stayTask';
};

type ISendShowArgs = ISendClickArgs;

type ISendPvArgs = {
    bundleId: string;
    componentName: string;
    // 埋点的pagename
    page: string;
    // 埋点的参数
    params?: object;
    // 是否发送到radar
    isSendRadar?: boolean;
    // 自定义雷达event的名字
    radarName?: string;
    // 是否是半屏，半屏是true，全屏为false
    coPage?: boolean;
};

const getDataInjection = () => {
    try {
        const dataInjection = getQuery('__data_injection__') || '{}';
        return JSON.parse(decodeURIComponent(dataInjection));
    } catch (e) {}
    return {};
};

export const validateP0Log = ({ action, params: originParams, type }) => {
    try {
        const bundleId = getPageProps().bundleId;
        const dataInjection = getDataInjection();
        let params = { ...originParams };

        if (dataInjection?.action === action) {
            params = {
                ...params,
                ...(dataInjection?.params || {}),
            };
        }

        const sendLog = () => {
            radar.event({
                name: action,
                eventType: 'CUSTOM_PO_LOG',
                params: { ...params, action },
            });
        };

        // 大卡商品卡点击和曝光 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1155148&fromPage=groupManage&groupId=8351&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['PRODUCT_CARD'].includes(action) && bundleId === 'EcommCommerce') {
            const { media_source, item_id } = params;
            // 增加非空检验
            if (!media_source || !item_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 购物车猜喜的商品卡片曝光埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1131512&fromPage=groupManage&groupId=8351&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['ECO_PRODUCT_CARD'].includes(action) && bundleId === 'MyShoppingCar') {
            const { product_id, media_source } = params;
            // 增加非空检验
            if (!product_id || !media_source) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 商品评价成功页猜喜的商品卡曝光埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1151668&fromPage=groupManage&groupId=8351&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['ECO_PRODUCT_CARD'].includes(action) && bundleId === 'EcommAddComment') {
            const { media_source, product_id } = params;
            // 增加非空检验
            if (!media_source || !product_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 订单详情页猜喜的商品卡片曝光埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1131511&fromPage=groupManage&groupId=8351&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['ECO_PRODUCT_CARD'].includes(action) && bundleId === 'OrderDetail') {
            const { product_id, media_source } = params;
            // 增加非空检验
            if (!product_id || !media_source) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 商详页店铺推荐模块的商品卡曝光埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1124367&fromPage=groupManage&groupId=8351&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['SHOP_RECOMMEND_CARD'].includes(action) && bundleId === 'Detail') {
            const { product_id } = params;
            // 增加非空检验
            if (!product_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }

        // 短视频双列主卡的加购点击埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1130450&fromPage=groupManage&groupId=8350&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (type === 'click' && ['ADD_TO_CART'].includes(action) && bundleId === 'EcomRecommendGoods') {
            const { media_id } = params;
            // 增加非空检验
            if (!media_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 商详页加购按钮的点击埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1130441&fromPage=groupManage&groupId=8350&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (type === 'click' && ['ADD_TO_CART'].includes(action) && bundleId === 'Detail') {
            const { item_id } = params;
            // 增加非空检验
            if (!item_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 订单详情加购点击埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1133080&fromPage=groupManage&groupId=8350&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['ADD_TO_CART'].includes(action) && bundleId === 'OrderDetail') {
            // 上报流量埋点
            sendLog();
        }
        // 商品详情页的购买点击埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1123592&fromPage=groupManage&groupId=8350&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['BUY_NOW'].includes(action) && bundleId === 'Detail') {
            const { product_id } = params;
            // 增加非空检验
            if (!product_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
        // 短视频双列购买点击埋点 https://data-track-sgp.corp.kuaishou.com/v3/#/entity/detail?entityCombineId=1128957&fromPage=groupManage&groupId=8350&submitDevPageType=ENTITY_NORMAL_EVENT&appName=TRINITY
        if (['BUY_NOW'].includes(action) && bundleId === 'EcomRecommendGoods') {
            const { item_id, photo_id, media_id } = params;
            // 增加非空检验
            if (!item_id || !photo_id || !media_id) {
                radar.event({
                    name: action,
                    eventType: 'CUSTOM_PO_LOG_PARAMS_ERROR',
                    params: { ...params, action },
                });
            }
            // 上报流量埋点
            sendLog();
        }
    } catch (error) {
        radar.event({
            name: action,
            eventType: 'CUSTOM_PO_LOG_FAIL',
            params: { ...params, action },
        });
    }
};

/**
 * @deprecated
 * 废弃这个用法，这个异步pv会有时序问题，需要afterPv才行；
 * 封装了一个 const { logger } = usePageContext()，可以保证pv的时序性，不需要借助afterPv，可以把afterPv废弃了；
 */
export const logger = {
    sendClick(args: ISendClickArgs) {
        const { action, isSendRadar = true, params = {}, radarName = action } = args;
        webLogger.sendLog({
            type: 'click',
            name: action,
            value: { ...params, ...getCommonParams() },
        });
        if (isSendRadar) {
            radar.event({ name: radarName, eventType: 'SHOP_KRN_CUSTOM_CLICK', params: { ...params, ...getCommonParams() } });
        }
        // 校验p0埋点的合法性
        validateP0Log({ action, params, type: 'click' });
    },
    sendTask(args: ISendTaskArgs) {
        const { action, isSendRadar = true, params = {}, radarName = action, type = 'backgroundTask' } = args;
        webLogger.sendLog({
            type,
            name: action,
            value: { ...params, ...getCommonParams() },
        });
        if (isSendRadar) {
            radar.event({ name: radarName, eventType: 'SHOP_KRN_CUSTOM_CLICK', params: { ...params, ...getCommonParams() } });
        }
        // 校验p0埋点的合法性
        validateP0Log({ action, params });
    },
    sendShow(args: ISendShowArgs) {
        const { action, isSendRadar = true, params = {}, radarName = action } = args;
        webLogger.sendLog({
            type: 'show',
            name: action,
            value: { ...params, ...getCommonParams() },
        });
        if (isSendRadar) {
            radar.event({ name: radarName, eventType: 'SHOP_KRN_CUSTOM_SHOW', params: { ...params, ...getCommonParams() } });
        }
        // 校验p0埋点的合法性
        validateP0Log({ action, params, type: 'show' });
    },
    sendPv(args: ISendPvArgs) {
        const { bundleId, componentName, page, params = {}, isSendRadar = true, radarName = page, coPage = getHalfByQuery() } = args;
        webLogger.sendAsyncPV({
            bundleId,
            componentName,
            pageMap: () => {
                return {
                    page,
                    params: { ...params, ...getCommonParams() },
                };
            },
            coPage,
        });
        if (isSendRadar) {
            radar.event({ name: radarName, eventType: 'SHOP_KRN_CUSTOM_PV', params: { ...params, ...getCommonParams() } });
        }
    },
    sendAsyncPV(params) {
        webLogger.sendAsyncPV(params);
    },
};

export const createPageContextLogger = () => {
    let isSendPv = false;
    // 曝光埋点的队列，等pv后发送
    let showQueue = [];
    // 点击埋点的队列，等pv后发送
    let clickQueue = [];
    // task 任务埋点队列
    let taskQueue = [];
    return {
        sendShow(args: ISendClickArgs) {
            if (isSendPv) {
                logger.sendShow(args);
            } else {
                showQueue.push(args);
            }
        },
        sendClick(args: ISendShowArgs) {
            if (isSendPv) {
                logger.sendClick(args);
            } else {
                clickQueue.push(args);
            }
        },
        // 不发送pv，直接发送其他埋点
        skipSendPv() {
            isSendPv = true;
            // 发送埋点
            showQueue.forEach(item => {
                logger.sendShow(item);
            });
            showQueue = [];
            clickQueue.forEach(item => {
                logger.sendClick(item);
            });
            clickQueue = [];
            taskQueue.forEach(item => {
                logger.sendTask(item);
            });
            taskQueue = [];
        },
        sendPv(args: ISendPvArgs) {
            logger.sendPv(args);
            isSendPv = true;
            // 发送埋点
            showQueue.forEach(item => {
                logger.sendShow(item);
            });
            showQueue = [];
            clickQueue.forEach(item => {
                logger.sendClick(item);
            });
            clickQueue = [];
            taskQueue.forEach(item => {
                logger.sendTask(item);
            });
            taskQueue = [];
        },
        sendAsyncPV(params) {
            logger.sendAsyncPV(params);
            isSendPv = true;
            // 发送埋点
            showQueue.forEach(item => {
                logger.sendShow(item);
            });
            showQueue = [];
            clickQueue.forEach(item => {
                logger.sendClick(item);
            });
            clickQueue = [];
            taskQueue.forEach(item => {
                logger.sendTask(item);
            });
            taskQueue = [];
        },
        sendTask(args: ISendTaskArgs) {
            if (isSendPv) {
                logger.sendTask(args);
            } else {
                taskQueue.push(args);
            }
        },
    };
};

export type ILogger = ReturnType<typeof createPageContextLogger>;
